// TS types for the VerityRx dashboard API (`/v1/dash`). These mirror the JSON
// shapes in the API contract; the engine verdict is always one of exactly three
// values, and a disposition (attorney action) is separate from the engine verdict.

export type Verdict = 'compliant' | 'grey' | 'non_compliant';


// `confirm_applies` / `confirm_not_applicable` (migration 0130) are a
// CONFIRMATION group's own verbs — see reviewVocab's isConfirmationType.
export type DispositionType =
  | 'accept'
  | 'override'
  | 'defer'
  | 'confirm_applies'
  | 'confirm_not_applicable';

export type SessionStatus = 'draft' | 'in_review' | 'completed' | 'finalized' | 'superseded';

export type GroupStatus = 'open' | 'partial' | 'done';

export type CommentVisibility = 'internal' | 'include_in_report';

export interface Disposition {
  type: DispositionType;
  final_verdict?: Verdict | null;
  override_reason?: string | null;
  override_tag?: string | null;
  // Who recorded it (Arvind 2026-09-29): 'team' means the VerityRx team on
  // counsel's behalf; `recorded_words` is the server's sentence saying so.
  recorded_as?: 'counsel' | 'team' | null;
  recorded_words?: string | null;
}

export interface Citation {
  title: string;
  // Null on a citation whose only link was dead at build time: the export keeps
  // the text and drops the link rather than sending a reviewer to a 404.
  url: string | null;
  primary_source: boolean;
  // BL-401. Both optional, both omitted rather than nulled when the record does
  // not hold them, so "we have no date" and "the source is undated" cannot be
  // confused. `as_of_date` is when WE last read the source; `effective_date` is
  // when the law itself took effect — a lawyer checking whether a rule applied
  // during the review window needs the second, not the first.
  as_of_date?: string;
  effective_date?: string;
  // Source policy (2026-09-29): present only on a last-resort citation whose
  // official page could not be read automatically and whose words were read in
  // a legal publisher's reproduction. `url` stays the official page; the
  // server's plain-words `source_label` is shown beside it.
  source_class?: string;
  source_label?: string;
}

export interface DrivingRule {
  code: string;
  summary: string;
  // Optional, present on snapshots exported since the per-rule metadata work
  // (older snapshots omit them — always feature-test):
  title?: string;
  level?: string; // practice | provider | drug | state
  outcome?: string; // this rule's own verdict on this finding
  primary?: boolean; // the finding's worst-verdict rule
  rule_class?: string; // remediation | confirmation
  citations?: Citation[]; // this rule's own sources (per-rule attribution)
  statement?: string; // the one-sentence verdict statement
}

export interface Progress {
  total: number;
  reviewed: number;
  pct: number;
}

// Reviewer readiness, present only for internal-admins. A queue that shows progress
// alone cannot tell "working through it" from "never able to log in" — both read 0%.
export interface CustomerReviewer {
  email: string;
  // True only when we positively know the invite was never completed. Unknown
  // (Cognito unreachable, or not configured locally) is false — never cry wolf.
  never_signed_in: boolean;
}

export interface MyCustomer {
  customer_id: string;
  name: string;
  vertical: string;
  // The caller's grant on THIS customer: 'attorney' | 'customer' |
  // 'internal_admin'. Decides which surface the row links to (/c vs /portal).
  my_role: string;
  capacities?: string[];
  // Operator console (migration 0074): the label and the remembered website.
  is_evaluation?: boolean;
  is_demo?: boolean;
  website?: string | null;
  reviewers?: CustomerReviewer[];
  // Named to match the API exactly. This was `latest_version`, a key the backend
  // never sends, so it read `undefined` and every card said "No snapshot yet".
  snapshot_version: number | null;
  binder_label: string | null;
  session_id: string | null;
  session_status: SessionStatus | null;
  completion_outcome?: ReviewOutcome | null;
  completion_undecided_count?: number | null;
  /** Prepared law-change notes counsel has not decided on this practice. */
  law_notices_waiting?: number;
  progress: Progress;
  // Practice groups (W4, UX-REDESIGN-BRIEF.md Addendum): null when this
  // workspace is not linked to another vertical's workspace under the same
  // practice. When present, `parts` names every linked workspace INCLUDING
  // this row's own — the switcher groups on `group_id` and labels each part
  // by its vertical. Optional so an older cached shape still narrows.
  practice_group?: PracticeGroupPart | null;
}

export interface FirmSummary { firm_id: string; name: string; role: 'admin' | 'attorney' }
export interface FirmMember { user_id: string; role: 'admin' | 'attorney'; email: string }
export interface FirmRosterRow extends Pick<MyCustomer, 'customer_id' | 'name' | 'vertical' | 'binder_label' | 'snapshot_version' | 'session_status' | 'completion_outcome' | 'completion_undecided_count' | 'progress'> {
  assigned_to: string | null;
  assignment_stale: boolean;
  due_date: string | null;
  risk: number;
  eligible_attorneys: string[];
  /** False when the viewer holds no live reviewer grant of their own on this practice. */
  can_open?: boolean;
  undecided_count: number | null;
}
export interface FirmRoster { items: FirmRosterRow[]; total: number; limit: number; offset: number; role: 'admin' | 'attorney'; practices_added_this_quarter: number }
export interface FirmReferral { request_id: string; firm: string; practice_name: string; contact_email: string; vertical: string; requested_at: string }
export interface FirmReferralPage { items: FirmReferral[]; total: number; limit: number; offset: number }

export interface PracticeGroupPart {
  group_id: string;
  parts: { customer_id: string; vertical: string; name: string }[];
}

// Operator console (screens/PracticeGroup.tsx). Same group shape as the
// switcher's, plus a per-part setup summary the operator card shows.
export interface OperatorPracticeGroupPart {
  customer_id: string;
  vertical: string;
  name: string;
  has_setup: boolean;
  people_count: number;
}

// A durable `practice_group_request` row (BL-693, package W6): the
// customer-visible "Also provide X services?" ask, recorded but not yet
// acted on. Additive on the operator response only -- the switcher's
// `PracticeGroupPart` (above) has no equivalent field.
export interface OperatorPracticeGroupRequest {
  id: string;
  customer_id: string;
  requested_vertical: string;
  at: string;
}

export interface OperatorPracticeGroup {
  group_id: string | null;
  parts: OperatorPracticeGroupPart[];
  pending_requests: OperatorPracticeGroupRequest[];
}

// A4b / BL-693: the self-serve "also provide the other part" ask
// (`WelcomeCard`'s `onRequestOtherPart`, `practice_group_request`,
// append-only) surfaced for the team to action. Read-only list + a resolve
// action -- no self-serve linking happens here; an operator still links the
// two workspaces on `screens/PracticeGroup.tsx` once the other part exists.
export interface OperatorPartRequest {
  request_id: string;
  customer_id: string;
  customer_name: string;
  vertical: string;
  requested_vertical: string;
  requested_at: string;
  requested_by_email: string | null;
  resolved: boolean;
  resolved_at: string | null;
}

// ---- User administration (internal-admin only) -----------------------------
// `cognito` is null when the pool is unset (dev) or unreachable — the admin must
// still be able to see and revoke grants when the Cognito egress path is down.
export interface UserGrant {
  access_id: string;
  customer_id: string;
  customer_name: string;
  role: string;
  revoked: boolean;
  revoked_at: string | null;
  invited_at: string | null;
}

export interface CognitoStatus {
  status: string | null;
  enabled: boolean;
}

export interface AdminUser {
  email: string;
  attorney_user_id: string;
  cognito: CognitoStatus | null;
  linked?: boolean;
  grants: UserGrant[];
  active_grants: number;
  // The identity's own role ('internal_admin' = on the VerityRx team); null when
  // the address has grants but no identity row yet (a pending invite).
  identity_role?: string | null;
}

export interface VerdictTally {
  compliant: number;
  grey: number;
  non_compliant: number;
}

export interface Rollup {
  total: number;
  accepted: number;
  overridden: number;
  /** Findings with at least one deferred rule; can overlap with `open`. */
  deferred: number;
  /** Findings not fully covered across every failing rule. */
  open: number;
  /** Findings fully covered across every failing rule. */
  settled?: number;
  decided?: number;
  pct: number;
}

export interface SessionSummary {
  ownership_facts?: string[];
  ownership_review?: import('../portal/api').OwnershipReview | null;
  session_id: string;
  status: SessionStatus;
  snapshot_version: number;
  binder_label: string | null;
  /** PMWALK P2: the practice's latest result is newer than this binder. */
  newer_result?: NewerResult | null;
  snapshot_id: string | null;
  snapshot_content_hash: string | null;
  // Snapshot vertical: 'telehealth_rx' | 'medspa_service'. Gates
  // vertical-specific surfaces (the cost calculator is telehealth-only).
  vertical: string | null;
  verdict_tally: VerdictTally;
  rollup: Rollup;
  has_binder_pdf: boolean;
  // What the attorney decided about a COMPLETED round, and the decision
  // reference for it — both null while the round is still open. The
  // reference used to live only in the Complete-review modal's success
  // state, gone the moment that modal was closed (LR-REPORT.md G-10f).
  completion_outcome?: ReviewOutcome | null;
  completion_undecided_count?: number | null;
  signed_by?: string | null;
  signed_at?: string | null;
  signed_open_count?: number | null;
  signature_pending?: boolean;
  can_sign?: boolean;
  // Who this request records as (Arvind 2026-09-29): 'counsel', 'team' (on
  // counsel's behalf, with a reason), or null (cannot record).
  recording_as?: 'counsel' | 'team' | null;
  completed_by_sentence?: string | null;
  recorded_by_team_count?: number;
  decision_hash?: string | null;
  is_historical_round?: boolean;
  // R9 (2026-09-29): the scope the team chose when this round was promoted
  // to compliance review. NULL/'whole_report' means every group is counsel's
  // (the pre-existing behaviour); 'legal_judgement' means `rollup`/
  // `completion_undecided_count` already exclude settled groups, and
  // `settled_count` says how many findings that excluded.
  counsel_scope?: 'legal_judgement' | 'whole_report' | null;
  settled_count?: number;
  // Round 2 (Arvind 2026-09-29 decision): who confirmed "I have reviewed
  // the other M findings" at promotion, and when. Both null when there was
  // nothing to confirm (whole-report scope, or zero settled findings).
  settled_confirmed_by_email?: string | null;
  settled_confirmed_at?: string | null;
  prior_round?: { session_id: string; snapshot_version: number; binder_label?: string | null; status: string; completion_outcome: ReviewOutcome | null } | null;
  version_changes?: {
    unchanged: Array<{ finding_id: string; provider_name: string | null; state_code: string; medication: string }>;
    requires_rereview: Array<{ finding_id: string; provider_name: string | null; state_code: string; medication: string }>;
  } | null;
}

// The per-group rollup omits `total`/`pct` (finding_count carries the total).
export interface GroupRollup {
  accepted: number;
  overridden: number;
  deferred: number;
  open: number;
}

export interface RuleGroupRow {
  rule_group_key: string;
  engine_verdict: Verdict;
  verdict_counts: { compliant: number; grey: number; non_compliant: number; provisional_compliant: number };
  label: string;
  finding_count: number;
  state_codes: string[];
  provider_count: number;
  rollup: GroupRollup;
  status: GroupStatus;
  // remediation | confirmation | null (legacy set-keyed snapshots). The board
  // renders confirmation groups in their own attest section (2026-08-08).
  rule_class?: string | null;
  // P1-2 (C3WORDS): this rule's grey findings by who can close them. Absent on
  // a server that predates the field.
  grey_kinds?: {
    awaiting_practice: number;
    awaiting_confirmation: number;
    awaiting_source?: number;
    legal_judgement: number;
    unknown: number;
  };
  // The counsel binder's own reference for this rule ("F2", "C1"), when the
  // export named one. Null on every snapshot built before the binder
  // composer started numbering (LR-REPORT.md G-10b) — never fabricated.
  binder_ref?: string | null;
  rule_codes?: string[];
  // R9 (2026-09-29): whether THIS round's scope (if narrowed) asks counsel
  // to review this group. Always true when the session has no narrow scope.
  in_counsel_scope: boolean;
  // COHERE2 P0-5, confirmation groups only: counted per rule. `to_confirm` is
  // every finding the confirmation holds open; `fail_another_rule` is how many
  // of those also fail a different rule (never this rule's own result).
  to_confirm?: number;
  fail_another_rule?: number;
  changed_since_last_round?: boolean;
  change_reason?: 'added' | 'removed' | 'changed' | null;
  in_changed_queue?: boolean;
}

// The regulatory Type a medication falls under — Types A/B/C in the medication
// grouping brief. Null for medspa findings (a service is not a medication) and for
// snapshots imported before the field existed.
export type MedicationType = 'controlled' | 'compounded' | 'standard';

export interface GroupMemberFinding {
  finding_id: string;
  state_code: string;
  provider_name: string | null;
  subject_label: string;
  medication_type: MedicationType | null;
  engine_verdict: Verdict;
  review_obligation?: {
    primary: string | null;
    requires_counsel_attestation: boolean | null;
    needs_counsel: boolean;
  };
  provisional?: boolean;
  disposition: Disposition | null;
  settles_if_decided?: boolean;
  // This finding's own binder reference for the drawer's rule, falling back
  // to the group's own reference. Null on an unnumbered snapshot.
  binder_ref?: string | null;
  citations?: Citation[];
  citation_scope?: 'rule' | 'finding' | 'missing_rule_citation' | 'missing_finding_citation';
  evidence_gap?: string | null;
  // PMWALK PM-71 / CN-16: what the binder prints for this finding, in words.
  questions?: string[];
  assumptions?: string[];
  product_declaration_labels?: string[];
  practice_facts?: {
    license_type?: string | null;
    medication?: string | null;
    state: string;
    scope?: Record<string, unknown> | null;
    good_faith_exam_workflow?: string | null;
    evaluated?: Array<{ path: string; value: unknown }> | null;
    blockers?: Array<{ label?: string; path?: string }>;
    rx_licenses?: Array<Record<string, unknown>>;
    dea?: Array<Record<string, unknown>>;
    state_csrs?: Array<Record<string, unknown>>;
    agreements?: Array<Record<string, unknown>>;
  } | null;
}

export interface GroupDetail {
  prior_round_items?: Array<{
    item: string;
    reason?: string | null;
    resolution: 'changed' | 'unchanged' | 'removed';
    resolution_words: string;
    cause?: string | null;
  }>;
  // Counsel's comments on this RULE in this round (PMWALK PM-39).
  rule_comments?: Array<{ comment_id: string; body: string; created_at: string | null }>;
  rule_group_key: string;
  rule_codes?: string[];
  engine_verdict: Verdict;
  label: string;
  rule_class?: string | null;
  binder_ref?: string | null;
  shared_rationale: string[];
  shared_citations: Citation[];
  state_authority?: Array<{ state_code: string; citations: Citation[]; missing_members: Array<{
    finding_id: string; provider_name: string | null; medication: string; reason: string;
  }> }>;
  findings: GroupMemberFinding[];
}

// "What we could not check" (LR-REPORT.md G-10a) — the export's `open_items`
// block, read verbatim from the stored snapshot. `assessed_count` is always
// the real finding count; `not_checked_count` is the honesty layer the
// engine never reached. Never render one without the other — a round can
// otherwise look 100% dispositioned while a whole slice of scope was never
// assessed at all.
export interface OpenItemsSummary {
  unattributed_prescribers: Array<Record<string, unknown>>;
  unmapped_products: Array<Record<string, unknown>>;
  missing_data: Array<Record<string, unknown>>;
  not_checked_count: number;
  assessed_count: number;
}

export interface StateRow {
  state_code: string;
  state_name: string;
  total: number;
  accepted: number;
  overridden: number;
  deferred: number;
  open: number;
  pct: number;
}

export interface StateFinding {
  product_declaration_labels?: string[];
  finding_id: string;
  provider_name: string | null;
  license_type: string | null;
  subject_label: string;
  medication_type?: string | null;
  dea_schedule: string | null;
  engine_verdict: Verdict;
  severity_tier: string | null;
  confirmation_only?: boolean;
  // The engine called this compliant but qualified it: one ambiguous
  // practice fact would make it a counsel question. Absent on legacy
  // payloads (BL-232).
  review_obligation?: {
    primary: string | null;
    requires_counsel_attestation: boolean | null;
    needs_counsel: boolean;
  };
  provisional?: boolean;
  driving_rules: DrivingRule[];
  citations: Citation[];
  disposition: Disposition | null;
  // Rule-level review progress (2026-08-08): how many of this finding's
  // failing rules a group-surface decision covers. Absent on legacy payloads.
  rules_covered?: number;
  rules_total?: number;
  comments_count: number;
}

export interface DispositionInput {
  type: DispositionType;
  final_verdict?: Verdict;
  override_reason?: string;
  override_tag?: string;
  // SCOPE (2026-08-09): a rule-group key means "this rule, for this finding"
  // — the group drawer's per-member controls pass the drawer's group so a
  // decision presented as one rule never covers the finding's other failing
  // rules. Absent = whole finding (the state-view flow).
  rule_group_key?: string;
  // Required when the VerityRx team records counsel's decision (Arvind
  // 2026-09-29); the API refuses a team decision without it.
  on_behalf_reason?: string;
}

export interface BulkDispositionResult {
  ok: true;
  applied_count: number;
}

export interface CommentInput {
  body: string;
  visibility: CommentVisibility;
}

export interface CompletePrecheck {
  total_count: number;
  open_count: number; // not fully settled — these block unless acknowledged
  /** How many distinct rules those undecided findings sit under (P1-17). */
  open_rule_count?: number;
  /** Undecided findings not already listed in the deferred-findings section. */
  open_list_count: number;
  open_findings: Array<{
    finding_id: string;
    state_code: string;
    subject_label: string;
    provider_name?: string | null;
    rule_labels?: string[];
  }>;
  deferred_count: number; // unique findings with at least one deferred failing rule
  deferred_findings: Array<{ finding_id: string; state_code: string; subject_label: string; provider_name?: string | null; rule_labels: string[] }>;
  accepted_count: number;
  rejected_count: number;
  fully_settled_count: number;
  settled_deferred_count: number;
  can_complete_clean: boolean;
  // R9 (2026-09-29): how many findings this round's narrow scope excludes as
  // already settled. Zero, and `counsel_scope` NULL/'whole_report', on a
  // round with no narrowed scope.
  settled_count?: number;
  counsel_scope?: 'legal_judgement' | 'whole_report' | null;
}

// What the attorney decided about the round as a whole. Required, and deliberately
// separate from `note`: "I'm signing off on this as final" and "I left comments, take
// another pass" are opposite outcomes that would otherwise arrive in the same free-text
// box, leaving nothing downstream able to tell which one happened.
export type ReviewOutcome = 'signed_off' | 'changes_requested';

export interface CompleteInput {
  open_item_ack: boolean;
  confirm_undecided_count?: number;
  outcome: ReviewOutcome;
  note?: string; // the attorney's overall note for this round
  on_behalf_reason?: string; // required when the team records the outcome
}

export interface DispositionSummary {
  total: number;
  accept: number;
  reject: number;
  /** Unique findings with a deferred rule; may overlap with `undecided`. */
  defer: number;
  undecided: number;
  changed_from_engine: number;
  with_comments: number;
  fully_settled?: number;
  settled_defer?: number;
  /** The decisions counsel made, counted as decisions (PD-5): a group verdict on one rule is
   *  one, however many findings it covers. Absent on a round completed before this existed. */
  decisions?: { accept: number; reject: number; defer: number; comments: number; findings_covered?: number };
}

export interface CompleteResult {
  status: string;
  outcome: ReviewOutcome;
  decision_hash: string;
  summary: DispositionSummary;
  undecided: number;
  deferred: number;
  counsel_scope?: 'legal_judgement' | 'whole_report' | null;
  scoped_total?: number;
  settled_count?: number;
  settled_confirmed_by_email?: string | null;
  settled_confirmed_at?: string | null;
  /** Rule groups counsel opened in this round, captured from the review surface. */
  reviewed_group_count?: number;
  reviewed_group_keys?: string[];
  completed_as?: 'counsel' | 'team';
  completed_by_sentence?: string | null;
  feedback_packet_url: string;
}

// Internal-admin only shapes.
export interface CreateCustomerResult {
  customer_id: string;
}

export interface CreateSnapshotResult {
  notifications_suppressed?: boolean;
  notifications_queued?: boolean;
  // Present when a completed round existed: the decisions carried onto this
  // version and the findings whose content moved (FR-28, wired 2026-09-07).
  carried_forward?: number;
  requires_rereview?: number;
  snapshot_id: string;
  version: number;
  binder_label: string | null;
  session_id: string;
  finding_count: number;
  group_count: number;
}

/**
 * What to call this binder on screen -- the ONE number every surface must
 * agree on: the number printed on the PDF itself (BINDERNUM, 2026-09-28).
 *
 * `version` is the per-customer upload/promotion COUNTER (it keys the PDF in
 * S3 and orders re-reviews) -- it is NOT what anyone says out loud, and it
 * undercounts whenever a build was never sent to counsel: a practice with
 * builds v14..v16 where only v16 was sent has `version` stuck at 14 while
 * the PDF prints "Binder v16" (the managed worker's build/generation number,
 * carried here as `label`, e.g. "Workspace v16"). Prefer the number the
 * label encodes; fall back to the counter only when the label carries no
 * number of its own (an older, date-stamped offline label).
 */
export function binderName(
  label: string | null | undefined,
  version: number | null | undefined,
  origin?: string | null,
): string | null {
  const trimmed = label?.trim();
  const numbered = trimmed
    ? /^(?:Binder|Workspace)?\s*v?(\d+)$/i.exec(trimmed)
    : null;
  if (numbered) return `v${Number(numbered[1])}`;
  // A workspace build's number is its generation and nothing else: the upload
  // counter would give two different binders the same name (COHERE3 P0-3).
  if (version != null && origin !== 'workspace') return `v${version}`;
  if (!trimmed) return null;
  return trimmed
    .replace(/^Binder\s+/i, '')
    .replace(/^Workspace\s+/i, '')
    .replace(/^(?=\d)/, 'v');
}

/**
 * A managed binder's own number (PMWALK P2): the label its PDF printed, which
 * the server derives through ONE function (`binder_number.artifact_label`)
 * and sends as `binder_label` with its `generation`. Every surface names a
 * built binder through this, never through the latest result's generation:
 * once a newer result exists that names a DIFFERENT binder.
 */
export function artifactBinderName(
  artifact: { binder_label?: string | null; generation?: number | null } | null | undefined,
): string | null {
  if (!artifact) return null;
  return binderName(
    artifact.binder_label ?? (artifact.generation != null ? `Workspace v${artifact.generation}` : null),
    null,
    'workspace',
  );
}

/** The latest result is newer than the binder shown (PMWALK P2). */
export interface NewerResult {
  generation: number;
  binder_label: string;
  built?: boolean;
}

/** "A newer result exists (v7)." -- said beside an older binder everywhere it
 *  is named, so the older binder keeps its own number. */
export function newerResultLine(newer: NewerResult | null | undefined, noun: 'Binder' | 'Report' = 'Binder'): string | null {
  if (!newer) return null;
  const name = binderName(newer.binder_label, null, 'workspace') ?? `v${newer.generation}`;
  return `A newer result exists (${noun} ${name}${newer.built === false ? ', not built yet' : ''}).`;
}

// ---- Cost calculator (BL-151) ------------------------------------------------
// The calculator prices requirements; it NEVER carries a verdict. Nothing in these
// shapes may ever hold a compliant/non_compliant/grey value — if you find yourself
// wanting one here, it belongs to the review surfaces, not this tab.

export type CostProviderType = 'MD_DO' | 'NP_APRN' | 'PA';
export type CostAddressTier =
  | 'virtual_mail'
  | 'fractional_physical'
  | 'commercial_lease';
export type CostMedicationType = 'controlled' | 'compounded' | 'standard';

export interface CostCoverageRow {
  state: string;
  provider_type: CostProviderType;
  count: number;
  already_licensed: number;
}

export interface CostSourced {
  value: number | null;
  source: string;
  as_of: string;
  note: string;
}

export interface CostAssumption {
  label: string;
  value: number | null;
  source: string;
  as_of: string;
  user_modified: boolean;
  note: string;
}

export interface CostDefaults {
  coverage: CostCoverageRow[];
  medication_types: CostMedicationType[];
  unmapped_provider_types: string[];
  snapshot_version: number | null;
  binder_label: string | null;
  address_tiers: Record<CostAddressTier, CostSourced>;
  default_address_tier: CostAddressTier;
  dea_annual: number | null;
  supervision_monthly: number | null;
  assumptions: CostAssumption[];
  caveats: string[];
  licence_fees_loaded: number;
  licence_fee_cells_total: number;
}

export interface CostEstimateInput {
  coverage: CostCoverageRow[];
  medication_types: CostMedicationType[];
  address_tier: CostAddressTier;
  supervision_enabled: boolean;
  supervision_states: string[];
  overrides: Partial<Record<'dea' | 'address' | 'supervision', number>>;
}

export interface CostLine {
  component: 'licence' | 'dea' | 'address' | 'supervision';
  state: string;
  detail: string;
  // null = the fee behind this requirement is NOT LOADED — render "fee not yet
  // loaded", never $0. A zero always arrives with a `reason` explaining it.
  first_year: number | null;
  ongoing_annual: number | null;
  reason: string;
  user_modified: boolean;
  // Where this line's unit cost was READ from — the issuing board's own fee page
  // for a licence, the primary document for DEA. Shown next to the number so a
  // priced line carries its citation the way a finding does. Empty when the user
  // overrode the unit cost: that figure is theirs, not the board's.
  source: string;
  source_as_of: string;
  source_note: string;
}

/** One official source behind an estimate, deduplicated across its lines. The
 *  server drops any source with no openable URL, so every entry here links. */
export interface CostSource {
  label: string;
  url: string;
  as_of: string;
  component: string;
}

export interface CostEstimate {
  lines: CostLine[];
  /** The distinct official sources behind the priced lines, rolled up so the
   *  provenance is one short list rather than a citation under every row. */
  sources: CostSource[];
  unloaded: string[];
  assumptions: CostAssumption[];
  caveats: string[];
  first_year_total: number;
  ongoing_annual_total: number;
  complete: boolean;
}

// ---- Remediation (Variant B — unreviewed gap summary) ----------------------- //
// Descriptive-only by construction: the backend has no code path that emits an
// instruction, a sequence, a deadline or a priority. Variant A (directive) is a
// separate, attorney-gated generator that does not exist in this app yet.

export interface RemediationCoordinates {
  state: string;
  provider: string | null;
  license_type: string | null;
  subject: string;
  medication_type?: string | null;
}

export interface RemediationRequirement {
  title: string;
  summary: string;
  code: string;
}

export interface RemediationCitation {
  title: string | null;
  url: string | null;
  primary_source: string | null;
}

export interface RemediationItem {
  coordinates: RemediationCoordinates;
  requirements: RemediationRequirement[];
  citations: RemediationCitation[];
  // Set when the finding's citations were all dropped at export time: the gap
  // still states (the engine verdict stands), with sourcing marked pending.
  citation_note: string | null;
  requirement_as_of: string;
  record: string;
  record_as_of: string;
  delta: string;
  // The fixed part-5 replacement (spec §2.2): gaps point to legal
  // determination; they never carry an action.
  note: string;
}

export interface RemediationSection {
  state: string;
  items: RemediationItem[];
}

export interface RemediationUndetermined {
  coordinates: RemediationCoordinates;
  reasons: string[];
  note: string;
}

export interface RemediationDoc {
  variant: 'B';
  variant_name: string;
  notice: string;
  attorney_reviewed_round: boolean;
  generated_at: string;
  // 'assessment' | 'practice_as_of' | 'upload' — where the dates came from;
  // 'upload' means the export carried no assessment date and the staleness
  // clock is anchored to the upload as a last resort.
  date_source: string;
  expires_at: string;
  expiry_provisional: boolean;
  summary: {
    gaps: number;
    undetermined: number;
    states_with_gaps: number;
    unattributed_order_count: number;
  };
  sections: RemediationSection[];
  undetermined: RemediationUndetermined[];
  not_assessed: {
    unattributed_prescribers: {
      username: string | null;
      order_count: number | null;
      states: string[];
      reason: string | null;
    }[];
    note: string;
  };
}

// ---- Operator console (internal-admin only; BL-344) -----------------------
// Every route behind these types refuses a non-admin on the server. The SPA
// hides the screens as a courtesy (components/AdminOnly.tsx); the boundary is
// dashboard/routers/operator.py, pinned by tests/test_operator_console.py.

export interface StateCoverage {
  state: string;
  verified: number;
  present: number;
  expected: number;
  pct: number;
}

export interface WorkspaceAccess {
  access_id: string;
  email: string;
  role: string;
  user_id?: string;
  assigned_prescriber_id?: string | null;
  never_signed_in?: boolean;
}

export interface WorkspaceMedspa {
  entitled_states: string[];
  site_states: string[];
  counts: { providers: number; sites: number; services: number; assignments: number };
  terms_accepted: boolean;
  readiness: {
    ready: boolean;
    ready_modulo_staleness: boolean;
    blockers: { code: string; label: string }[];
    fingerprint: string | null;
    ready_since: string | null;
    last_built_fingerprint: string | null;
  };
  coverage?: StateCoverage[];
}

export interface ReviewRollup {
  snapshot_version: number | null;
  binder_label: string | null;
  session_id: string | null;
  session_status: SessionStatus | null;
  completion_outcome?: ReviewOutcome | null;
  completion_undecided_count?: number | null;
  progress: Progress;
  counsel_scope?: 'legal_judgement' | 'whole_report' | null;
  settled_count?: number;
  settled_confirmed_by_email?: string | null;
  settled_confirmed_at?: string | null;
}

export interface WorkspaceRow {
  results_held: boolean;
  auto_release_enabled: boolean;
  // R9 (2026-09-29): the practice-level policy set on the Account page.
  // 'not_used' hides promotion entirely and the practice screens never say
  // a lawyer reviewed anything.
  counsel_review_mode: 'every_report' | 'when_chosen' | 'not_used';
  pipeline_stage: string;
  age_days: number | null;
  idle_days: number | null;
  stuck: boolean;
  health: 'active' | 'stuck';
  blocker: string | null;
  next_action: string;
  customer_id: string;
  name: string;
  vertical: string;
  is_evaluation: boolean;
  is_demo: boolean;
  status: 'active' | 'archived';
  website: string | null;
  // How many practice people may hold a login (BL-379); the team edits it.
  practice_seat_cap: number;
  created_at: string | null;
  last_activity: string | null;
  access: WorkspaceAccess[];
  review: ReviewRollup;
  medspa: WorkspaceMedspa | null;
}

export interface AttentionRow {
  customer_id: string; customer_name: string; vertical: string;
  state: string; label: string; waiting_on: 'team' | 'counsel' | 'practice';
  started_at: string; event_type: string; action_path: string; age_days: number;
}
export interface AttentionPayload {
  rows: AttentionRow[]; counts: Record<string, number>; total: number; customer_count: number;
}

export interface WorkspaceSnapshot {
  snapshot_id: string;
  version: number;
  /** BL-719: where it came from -- an export uploaded on the Binder tab
   *  (built offline, the pre-workspace path) or a version the workspace
   *  promoted from the practice's own assessment. */
  origin?: 'uploaded' | 'workspace';
  binder_label: string | null;
  created_at: string | null;
}

export interface WorkspaceEvent {
  action: string;
  actor_role: string | null;
  actor_user_id?: string | null;
  target_type: string | null;
  occurred_at: string | null;
}

export interface WorkspaceDetail extends WorkspaceRow {
  /** Existing assigned-firm relationships; empty means no assignment is on file. */
  counsel_engagements?: Array<{
    firm_id: string;
    firm_name: string;
    counsel_access_live: boolean;
  }>;
  demo_counsel_decision?: { type: string; reason: string | null } | null;
  snapshots: WorkspaceSnapshot[];
  recent_activity: WorkspaceEvent[];
  first_results_released_at?: string | null;
  result_transition?: WorkspaceEvent | null;
}

export interface SitePreviewSummary {
  fetched: boolean;
  fetch_note: string;
  pages_read: number;
  pages_skipped: number;
  // Pages read from a locations directory (a chain keeps its people there).
  location_pages_read?: number;
  // And the ones it could not reach: a branch listed in the directory whose
  // own page — the only place its address is written — was not read.
  location_pages_unread?: number;
  // Of those, how many are branches the draft has NO row for. The index
  // usually carries every address itself, so the two numbers differ.
  location_addresses_missing?: number;
  location_note?: string;
  locations: number;
  locations_without_state: number;
  locations_by_state: { state: string; locations: number }[];
  people: number;
  services: number;
}

export interface SitePreview {
  website: string;
  summary: SitePreviewSummary;
  // The same drafts the portal's "Scan my website" returns; handed to the new
  // workspace through sessionStorage so the scan is not run twice.
  drafts: unknown;
}

export interface CreateWorkspaceInput {
  name: string;
  vertical: 'medspa_service' | 'telehealth_rx';
  website?: string | null;
  is_evaluation: boolean;
  is_demo?: boolean;
  states: string[];
}

// ---- Files a practice hands us (BL-350) ------------------------------------
export interface UploadTeamRow {
  upload_id: string;
  display_label: string;
  resend_count?: number;
  file_name: string;
  size_bytes: number | null;
  status: string;
  status_label: string;
  detail: string | null;
  patient_details_found: boolean;
  phi_categories: string[];
  mapping: Record<string, string> | null;
  s3_key: string;
  created_at: string | null;
  received_at: string | null;
  processed_at: string | null;
  expires_at: string | null;
  deleted_at: string | null;
  data_source_id: string | null;
}

export interface UploadsTeamList {
  files: UploadTeamRow[];
  available: boolean;
  roles: { keep: string[]; identifier: string[]; other: string[] };
}

// ---- Declared data sources, team status (R4/R6) ----------------------------
export interface DataSourceBlockInfo {
  item_key: string;
  reason: string;
  reason_label: string;
  blocked_at: string | null;
  blocked_by_user_id: string | null;
  who: string | null;
}

export interface DataSourceStatusRow {
  id: string;
  kind: string;
  kind_label: string;
  name: string;
  created_at: string | null;
  archived_at: string | null;
  received_at: string | null;
  period_covered: { from: string | null; to: string | null } | null;
  status: 'ok' | 'not_received' | 'missing_column' | 'stale';
  detail: string | null;
  blocked: DataSourceBlockInfo | null;
}

export interface DataSourceStatusList {
  sources: DataSourceStatusRow[];
  freshness_window_days: number;
}

export interface StillNeededOperatorList {
  items: Array<{
    item_key: string;
    first_asked_at: string | null;
    what: string;
    why: string;
    waiting_on: string;
    how: string;
    blocked: boolean;
    block: { reason_label: string; blocked_at: string | null; blocked_by_user_id: string | null; who: string | null } | null;
  }>;
  open_count: number;
  reminder_count: number;
  first_item: StillNeededOperatorList['items'][number] | null;
}

export interface UploadColumn {
  index: number;
  name: string;
  role: string;
  detected_role: string;
  basis: string;
  fill_rate: number;
  distinct: number;
  shape: Record<string, number>;
  samples: string[] | null;
  phi: string[];
  /** The heading looked like a value and is shown as "(column N)". */
  name_withheld?: boolean;
}

export interface UploadStructure {
  file: string;
  kind: string;
  rows: number;
  header_row: number | null;
  /** No header row at all: every column is "(column N)" and row one is data. */
  headerless?: boolean;
  columns: UploadColumn[];
  phi_categories: string[];
  recognized: boolean;
  unmapped: string[];
  notes: string[];
}

export interface UploadGate {
  passed: boolean;
  findings: { path: string; category: string }[];
  strings_checked: number;
  identifier_values: number;
}

export interface UploadReport extends UploadTeamRow {
  structure: UploadStructure | null;
  gate: UploadGate | null;
}

// ---------------------------------------------------------------------------
// Practice source notices (BL-562)
//
// One notice is a prepared reading of ONE immutable source event, for ONE
// practice, reviewed by counsel at one exact version. Three facts stay apart
// everywhere in these types, because merging them is the class of defect this
// surface exists to avoid:
//
//   * the REVIEW STATE — what counsel recorded, about a specific version;
//   * the BASIS        — what was captured when that version was written;
//   * CURRENTNESS      — whether that basis still holds, as of a separate
//                        observation carrying its own timestamp.
//
// Nothing here publishes, sends or delivers anything.
// ---------------------------------------------------------------------------

/** What counsel recorded. Stored on the notice. */
export type NoticeReviewState = 'awaiting_review' | 'accepted' | 'rejected' | 'deferred';

/**
 * What a person should act on. `accepted_but_outdated` is DERIVED by the
 * server from the stored state plus a fresh basis reading: an acceptance whose
 * basis has since moved is not settled work, and filtering on the stored state
 * alone would hide it.
 */
export type EffectiveReviewStatus = NoticeReviewState | 'accepted_but_outdated';

export type NoticeDecisionKind = 'accept' | 'reject' | 'defer';

/** What the SERVER says this caller may do, from their real grants. */
export interface NoticeCapabilities {
  can_prepare: boolean;
  can_amend: boolean;
  can_review: boolean;
  /** The capacity a decision would be recorded under; null if none. */
  reviewing_as: { grant_id: string; grant_role: string } | null;
  acting_identity_role: string;
  note: string;
}

/** Human names for the codes in this payload, from the engine's own catalogs. */
export interface NoticeLabels {
  services: Record<string, string>;
  credentials: Record<string, string>;
}

/**
 * Whether a reviewed version's basis still holds. `not_asserted` is what a
 * write receipt says: it recorded history and makes no claim about now.
 */
export interface NoticeCurrentness {
  state: 'unchanged' | 'changed' | 'unknown' | 'not_asserted';
  reason?: string | null;
  /** Which halves of the material differ — the precise conflict causes. */
  moved?: string[];
  /** Places the capture could not establish an expected value. */
  unknown_inputs?: string[];
  observed_at?: string;
  needs_new_version?: boolean;
  /** The version the comparison was actually made against. */
  compared_version?: number | null;
  association_now?: { matched?: boolean; reason?: string | null };
  /** Only on an existing-notice retry: the digest of the world NOW. */
  basis_digest_now?: string;
}

/**
 * The watched page. Detection-time facts (`event_*`) and current watch
 * metadata (`watch_*`) are separate keys on purpose: a historical version must
 * never be hydrated with today's metadata under its old capture date.
 */
export interface NoticeSourceState {
  mode: 'readable' | 'unreadable' | 'not_watched';
  content_hash: string;
  active: boolean;
  frozen: boolean;
  frozen_reason?: string | null;
  last_error?: string | null;
  /** An ATTEMPT, successful or not. Never a retrieval date on its own. */
  last_attempt_at?: string | null;
  successful_read_at?: string | null;
  successful_read_available?: boolean;
  consecutive_failures?: number;
  event_kind: string;
  event_detected_at?: string | null;
  event_url?: string | null;
  event_state_code?: string | null;
  event_layer?: string | null;
  event_summary?: Record<string, unknown>;
  event_instruments?: string[];
  event_new_hash?: string | null;
  event_previous_hash?: string | null;
  event_snapshot_key?: string;
  event_previous_snapshot_key?: string;
  watch_url?: string | null;
  /** The operator's own words for the page. NOT an official title. */
  watch_label?: string | null;
  watch_label_is_official_title: boolean;
  watch_layer?: string | null;
  watch_role?: string | null;
  watch_instruments?: string[];
}

/** [state, service, credential, modality, party, provider_id, site_id] */
export type HitIdentity = string[];

export interface NoticeHit {
  identity: HitIdentity;
  dependencies: string[][];
  provenance: string;
  cites_source: boolean;
  source_refs?: unknown[];
}

export interface NoticeAssociation {
  matched: boolean;
  reason: string | null;
  /** Matched SERVICE-AND-CREDENTIAL combinations. Not people or locations. */
  matched_determinations?: number;
  determinations?: string[][];
  hits?: NoticeHit[];
  unvalidated?: NoticeHit[];
  candidates?: number;
  unproven?: number;
  states?: string[];
}

/** The scoped projection the comparison runs over. Entries are [key, value]. */
export interface NoticeMaterial {
  protocol: string;
  states: string[];
  entitled: string[];
  arrangements: [string[], Record<string, unknown>][];
  results: [string[], Record<string, unknown> | string][];
  reference: [string[], Record<string, unknown> | string][];
  source: Record<string, unknown>;
  engine_version: string;
}

/** The server-derived half of a version, apart from anything a person wrote. */
export interface NoticeObserved {
  protocol: string;
  observed_at: string;
  engine_version: string | null;
  origin_event_id: string;
  association: NoticeAssociation;
  source: NoticeSourceState;
  material: NoticeMaterial;
  material_digest: string;
}

export interface NoticeVersion {
  version: number;
  content_hash: string;
  /** Attributed to a person. Never mixed into the observed facts. */
  staff_note: string | null;
  interpretation: string | null;
  observed: NoticeObserved;
  limitations: string[];
  basis_protocol: string;
  engine_version: string | null;
  basis_observed_at: string;
  author_user_id: string;
  author_kind: string;
  created_at: string;
}

export interface NoticeDecisionRow {
  decision_revision: number;
  version: number;
  content_hash: string;
  decision: NoticeDecisionKind;
  reason: string | null;
  actor_user_id: string;
  grant_id: string;
  grant_role: string | null;
  basis_observed_at: string | null;
  decided_at: string;
}

export interface NoticeRow {
  id: string;
  origin: string;
  origin_event_id: string;
  review_state: NoticeReviewState;
  version: number;
  content_hash: string | null;
  decision_revision: number;
  basis_observed_at: string | null;
  association: NoticeAssociation | null;
  /** What was captured when the version was written. */
  source_as_captured: NoticeSourceState | null;
  /** What the source looks like NOW, from this page's own observation. */
  source_now: NoticeSourceState | null;
  currentness: NoticeCurrentness;
  effective_review_status: EffectiveReviewStatus;
}

/**
 * The practice's own basis, read in the same statement as the page. An empty
 * queue has two very different causes and they need different words: nothing
 * prepared yet, or nothing that COULD be prepared.
 */
export interface PracticeBasis {
  entitled_states: string[];
  assessed_determinations: number;
  recorded_arrangements: number;
  missing_basis: string | null;
  missing_basis_message: string | null;
}

export interface NoticeQueuePage {
  notices: NoticeRow[];
  watch_status: import('../shared/lawWatchStatus').LawWatchStatus;
  labels: NoticeLabels;
  capabilities: NoticeCapabilities;
  practice_basis: PracticeBasis;
  page_size: number;
  has_more: boolean;
  next_cursor: string | null;
  /** THIS page's observation. Pages are separate readings, not one snapshot. */
  observed_at: string;
  count_label: string;
  client_scope_note: string;
  note: string;
}

/** Who a user id is, for the people holding a grant on THIS practice. */
export interface NoticePerson {
  email: string | null;
  grants: { role: string; revoked: boolean }[];
  any_live_grant: boolean;
}

export interface NoticeDetailView {
  id: string;
  labels: NoticeLabels;
  /** Presentation only: a history nobody can read names nobody. */
  people: Record<string, NoticePerson>;
  origin: string;
  origin_event_id: string;
  review_state: NoticeReviewState;
  current_version: number;
  decision_revision: number;
  created_by_user_id: string;
  observed_at: string;
  versions: NoticeVersion[];
  decisions: NoticeDecisionRow[];
  currentness: NoticeCurrentness;
  capabilities: NoticeCapabilities;
  effective_review_status: EffectiveReviewStatus;
  accepted_but_outdated: boolean;
  note: string;
}

export interface NoticeCandidate {
  origin_event_id: string;
  eligible: boolean;
  reason: string | null;
  state: string | null;
  event_kind: string;
  detected_at: string | null;
  source_label: string | null;
  source_label_is_official_title: boolean;
  source_url: string | null;
  layer: string | null;
  source_access_mode: string;
  matched_determinations: number;
  determinations: string[][];
}

/**
 * ONE PAGE of source events this practice could have a notice prepared for.
 *
 * Every row the server read is here, in keyset order, eligible or not — which
 * is what makes `next_cursor` safe: it names the last row actually returned,
 * so nothing between two pages can be skipped. The earlier shape read 120
 * events and returned the best 25, which dropped 95 rows it had read and left
 * older eligible events impossible to reach at all.
 *
 * Every count describes THIS page. There is no global total and the server
 * does not invent one.
 */
export interface NoticeCandidateList {
  candidates: NoticeCandidate[];
  labels: NoticeLabels;
  capabilities: NoticeCapabilities;
  observed_at?: string;
  page_size: number;
  has_more: boolean;
  next_cursor: string | null;
  /** Rows read for this request. Not how many events exist. */
  examined: number;
  eligible_on_page: number;
  count_label: string;
  note: string;
  nothing_here_because?: string;
}

/**
 * What a write returns: a HISTORICAL receipt. `currentness` is always an
 * object and defaults to `{ state: 'not_asserted' }` — the response is never
 * evidence that the basis is still current, so the UI refetches instead of
 * caching an implied approval.
 */
export interface NoticeReceipt {
  notice_id: string;
  version: number;
  content_hash: string;
  basis_observed_at: string;
  basis_protocol: string;
  basis_digest: string;
  engine_version: string | null;
  currentness: NoticeCurrentness;
  note: string;
  created?: boolean;
  outcome?: string;
  author_user_id?: string;
  created_by_user_id?: string;
  current_version_author_user_id?: string;
  association_now?: { matched?: boolean; reason?: string | null };
  decision?: NoticeDecisionKind;
  decision_revision?: number;
  review_state?: NoticeReviewState;
  grant_id?: string;
  grant_role?: string;
  replayed?: boolean;
  superseded_version?: number;
}

// ---- Research publishing (internal-admin only; spec §5) --------------------
// The thin staff panel behind /admin/research. Every route refuses a
// non-admin on the server (dashboard/routers/operator_research.py); the SPA
// route is a courtesy. A draft preview is counts and a hash, never the prose.

export interface ResearchDraftPreview {
  title: string | null;
  version: string | null;
  collected_on: string | null;
  topics: number;
  cards: number;
  sources: number;
  content_hash: string;
  /** Titles of this document's topics that no practice is served today,
   *  because the use case behind them is switched off. The counts above are
   *  of the whole document either way: the team publishes all of it. */
  hidden_from_practices?: string[];
}

export interface ResearchDraftRow {
  id: string;
  content_hash: string;
  created_by: string;
  created_at: string;
  title: string | null;
  version: string | null;
  topics: number;
  sources: number;
  cards: number;
  reviews: number;
  publication_id: string | null;
}

export interface ResearchDraftReview {
  id: string;
  draft_id: string;
  content_hash: string;
  actor_id: string;
  method: 'human' | 'ai_assisted';
  note: string;
  reviewed_at: string;
}

export interface ResearchDraftDetail {
  id: string;
  content_hash: string;
  created_at: string;
  created_by: string;
  preview: ResearchDraftPreview;
  validation: { ok: boolean; profile: string };
  publication: { id: string; status: string; published_at: string } | null;
  reviews: ResearchDraftReview[];
  reviews_next: { time: string; id: string } | null;
}

export interface ResearchImportResult {
  id: string;
  content_hash: string;
  created_at: string;
  created_by: string;
  preview: ResearchDraftPreview;
  validation: { ok: boolean; profile: string };
}

export interface ResearchCurrentPublication {
  id: string;
  version: string;
  content_hash: string;
  status: 'published' | 'superseded' | 'withdrawn';
  published_at: string;
  capacity: 'internal_admin' | 'system_import';
  validation_profile: string;
}

export interface ResearchPublicationEventRow {
  id: number;
  publication_id: string;
  version: string;
  action: 'imported' | 'published' | 'superseded' | 'withdrawn';
  public_reason: string;
  happened_at: string;
}

export interface ResearchPublicationHistory {
  events: ResearchPublicationEventRow[];
  next: number | null;
  current: ResearchCurrentPublication | null;
}

/** One recorded source check, as the team's queue shows it (C spec §4). The
 *  summary is the diff in words; `cards` are the items in the CURRENT
 *  publication that rest on the source, so a change is never just an id. */
export interface ResearchSourceChangeRow {
  id: number;
  source_id: string;
  kind: 'first_seen' | 'text_changed' | 'unreadable' | 'readable_again';
  checked_at: string;
  diff_summary: string | null;
  significant: boolean;
  fetched_via: 'direct' | 'egress';
  url: string;
  scope: 'state' | 'federal' | 'public';
  state_code: string | null;
  source_kind: string;
  last_checked: string | null;
  last_error: string | null;
  consecutive_failures: number;
  title: string | null;
  publisher: string | null;
  in_current_publication: boolean;
  cards: { topicId: string; id: string; title: string }[];
}

export interface ResearchSourceChangeQueue {
  changes: ResearchSourceChangeRow[];
  next: number | null;
  current: ResearchCurrentPublication | null;
}

/** What a change means, recorded once. `updated_in_release` is the only one
 *  that closes a change for customers, and it names the release. */
export type ResearchDisposition = 'no_effect' | 'needs_update' | 'updated_in_release';

/** One practice's research request as the TEAM sees it in the cross-customer
 *  queue (BL-614). Every sentence in it is the server's own — the same words
 *  the practice reads in its own workspace — so nothing here is re-worded on
 *  this side. `sent_at` is null only for a request that was never sent, which
 *  is reachable with `status=all`; `queued_at` is the one ordering key and
 *  the cursor is a point on it. */
export interface ResearchCustomerRequestRow {
  customer_id: string;
  customer_name: string;
  case_id: string;
  topic: string;
  topic_label: string;
  kind: 'about_reference' | 'new_subject';
  kind_label: string;
  question_code: string;
  question_label: string;
  subject_text: string;
  status: 'draft' | 'in_review' | 'reviewed' | 'archived';
  status_label: string;
  current_revision: number;
  action_revision: number;
  assignee_id: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  queued_at: string;
  age_hours: number;
  reference_changed: boolean;
  last_actor: {
    user_id: string;
    email: string | null;
    capacity_label: string;
    action: string;
    at: string;
  } | null;
}

export interface ResearchCustomerRequestQueue {
  requests: ResearchCustomerRequestRow[];
  /** The whole queue, not the page: what the tab counts. */
  count: number;
  /** False when the workspace fan-out hit its bound, so `count` is a floor. */
  count_exact: boolean;
  next: string | null;
  truncated: boolean;
  customers_scanned: number;
  customer_limit: number;
}
